import { useEffect, useState } from "react";
import type { CSSProperties } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";

import {
  bulkUpdateTerminologyItems,
  listTerminologyFilterOptions,
  listTerminologyItems,
  type ListTerminologyFilters,
  type TerminologyItem,
} from "@/api/terminology";
import { useToast } from "@/components/toast";
import {
  buildTerminologyListTourSteps,
  runTour,
  shouldAutoRunTour,
} from "@/tours/terminologyTour";
import { itemTypeLabel, langTagLabel, statusLabel } from "./labels";

const PAGE_SIZE = 50;

const TYPE_TABS: Array<{ key: string; label: string }> = [
  { key: "", label: "Все" },
  { key: "glossary_term", label: "Термины" },
  { key: "abbreviation", label: "Аббревиатуры" },
  { key: "term_alias", label: "Aliases" },
];

const ROLE_TABS: Array<{ key: string; label: string; hint: string }> = [
  { key: "", label: "Все", hint: "" },
  {
    key: "trusted_phrase_memory",
    label: "Trusted phrases",
    hint: "Enforcement-кандидаты, перешедшие в фразовую память (replace_alias)",
  },
];


export default function AdminTerminology() {
  const navigate = useNavigate();
  const auth = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(params.get("q") ?? "");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const filters: ListTerminologyFilters = {
    q: params.get("q") ?? undefined,
    item_type: params.get("item_type") ?? undefined,
    domain: params.get("domain") ?? undefined,
    status: params.get("status") ?? undefined,
    source_lang: params.get("source_lang") ?? undefined,
    target_lang: params.get("target_lang") ?? undefined,
    source_key: params.get("source_key") ?? undefined,
    source_role: params.get("role") ?? undefined,
    mode: (params.get("mode") as "hard" | "hint" | null) ?? undefined,
    limit: PAGE_SIZE,
    offset: Number(params.get("offset") ?? 0) || 0,
  };

  const itemsQuery = useQuery({
    queryKey: ["terminology-items", filters],
    queryFn: async () => {
      const r = await listTerminologyItems(filters);
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
  });

  const optsQuery = useQuery({
    queryKey: ["terminology-filter-options"],
    queryFn: async () => {
      const r = await listTerminologyFilterOptions();
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
    staleTime: 5 * 60_000,
  });

  // sync search input with URL when params change externally
  useEffect(() => {
    setSearchInput(params.get("q") ?? "");
  }, [params]);

  // Автозапуск тура при первом заходе на страницу (один раз на браузер;
  // потом — только по кнопке «? Тур»).
  // ВАЖНО: НЕ возвращаем cleanup с clearTimeout. В React.StrictMode (dev)
  // effect гоняется дважды (setup→cleanup→setup); cleanup отменил бы
  // единственный запланированный запуск, а второй проход уже видит
  // localStorage="done" (его выставил shouldAutoRunTour на первом проходе)
  // и тур не стартует. Поэтому таймер не отменяем.
  useEffect(() => {
    if (!shouldAutoRunTour("mt:tour:terminology-list")) return;
    window.setTimeout(() => runTour(buildTerminologyListTourSteps()), 500);
  }, []);

  const role = params.get("role") ?? "";
  const items = itemsQuery.data?.items ?? [];
  const total = itemsQuery.data?.total ?? 0;
  const offset = filters.offset ?? 0;
  const reviewer =
    (auth.user?.profile?.preferred_username as string | undefined) ??
    (auth.user?.profile?.email as string | undefined) ??
    "anonymous";

  // Сбрасываем selected при смене filters/page.
  const filtersKey = JSON.stringify(filters);
  useEffect(() => {
    setSelected(new Set());
  }, [filtersKey]);

  const bulkMutation = useMutation({
    mutationFn: async (patch: Record<string, unknown>) => {
      const r = await bulkUpdateTerminologyItems({
        reviewer,
        item_ids: Array.from(selected),
        patch,
      });
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
    onSuccess: (data) => {
      toast(
        `${data.success_count} обновлено · ${data.error_count} ошибок`,
        data.error_count > 0 ? "warning" : "success",
      );
      setSelected(new Set());
      void qc.invalidateQueries({ queryKey: ["terminology-items"] });
    },
    onError: (err: Error) => toast(`Не применено: ${err.message}`, "error"),
  });

  function setFilter(key: string, value: string | undefined): void {
    const next = new URLSearchParams(params);
    if (value == null || value === "") next.delete(key);
    else next.set(key, value);
    next.delete("offset");
    setParams(next, { replace: true });
  }
  function setOffset(o: number): void {
    const next = new URLSearchParams(params);
    if (o > 0) next.set("offset", String(o));
    else next.delete("offset");
    setParams(next, { replace: true });
  }
  function resetFilters(): void {
    setParams(new URLSearchParams(), { replace: true });
    setSearchInput("");
  }
  function submitSearch(): void {
    setFilter("q", searchInput.trim() || undefined);
  }

  function toggleSelect(id: string): void {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }
  function toggleSelectAll(): void {
    if (selected.size >= items.length && items.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(items.map((it) => it.item_id)));
    }
  }
  const allChecked = items.length > 0 && selected.size >= items.length;
  const someChecked = selected.size > 0 && !allChecked;

  /** Простая bulk-операция: prompt'ом запрашиваем значение для одного
   *  поля и шлём patch на selected items. Backend rejects попытки изменить
   *  candidate-типы — обработка через success_count/error_count. */
  function bulkSetField(field: "domain" | "notes"): void {
    const label =
      field === "domain"
        ? "Установить новый домен (например, Cardiology):"
        : "Заметка (notes), будет применена ко всем выбранным:";
    const value = window.prompt(label);
    if (value == null || value.trim() === "") return;
    bulkMutation.mutate({ [field]: value.trim() });
  }

  return (
    <div className="rl-shell">
      {/* SIDEBAR */}
      <aside className="rl-side">
        <div className="rl-side__head">
          <span style={{ fontWeight: "var(--fw-semi)", fontSize: "var(--fs-md)" }}>
            Терминология
          </span>
        </div>
        <div className="rl-side__body">
          <div className="rl-side__title">По домену</div>
          <DomainList
            options={optsQuery.data?.domains ?? []}
            active={filters.domain}
            onPick={(v) => setFilter("domain", v)}
          />
          <div className="rl-side__title">По источнику</div>
          <DomainList
            options={optsQuery.data?.source_keys ?? []}
            active={filters.source_key}
            onPick={(v) => setFilter("source_key", v)}
            mono
          />
          <div className="rl-side__title">По статусу</div>
          <DomainList
            options={optsQuery.data?.statuses ?? []}
            active={filters.status}
            onPick={(v) => setFilter("status", v)}
            translateLabel={statusLabel}
          />
        </div>
      </aside>

      {/* MAIN */}
      <main className="rl-main">
        {/* FILTERS */}
        <div className="rl-filters">
          <div className="rl-filters__group" data-tour="term-type">
            <span className="rl-filters__label">Тип</span>
            <div className="mt-segmented">
              {TYPE_TABS.map((t) => (
                <button
                  key={t.key || "all"}
                  className={
                    "mt-segmented__item" +
                    ((filters.item_type ?? "") === t.key ? " is-active" : "")
                  }
                  onClick={() => setFilter("item_type", t.key)}
                  type="button"
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div className="rl-filters__sep" />
          <div className="rl-filters__group">
            <span className="rl-filters__label">Роль</span>
            <div className="mt-segmented">
              {ROLE_TABS.map((t) => (
                <button
                  key={t.key || "all"}
                  className={
                    "mt-segmented__item" + (role === t.key ? " is-active" : "")
                  }
                  onClick={() => setFilter("role", t.key || undefined)}
                  type="button"
                  title={t.hint}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div className="rl-filters__sep" />
          <div className="rl-filters__group" data-tour="term-search">
            <span className="rl-filters__label">Поиск</span>
            <input
              className="mt-input mt-input--sm"
              style={{ minWidth: 220 }}
              placeholder="термин, alias, источник…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitSearch();
              }}
            />
            <button
              className="mt-btn mt-btn--sm mt-btn--ghost"
              onClick={submitSearch}
              type="button"
            >
              Найти
            </button>
          </div>
          <span className="mt-toolbar__sep" style={{ flex: 1 }} />
          <button
            className="mt-btn mt-btn--sm mt-btn--ghost"
            onClick={() => runTour(buildTerminologyListTourSteps())}
            type="button"
            title="Короткий тур по странице"
          >
            ? Тур
          </button>
          <button
            className="mt-btn mt-btn--sm mt-btn--ghost"
            onClick={resetFilters}
            type="button"
          >
            Сбросить
          </button>
        </div>

        {/* CHIPS */}
        {(hasActiveChips(filters) || role) && (
          <div className="rl-chips">
            {chipsList(filters, role).map((c) => (
              <span key={c.key} className="mt-chip">
                {c.label}
                <button
                  className="mt-chip__close"
                  onClick={() => setFilter(c.key, undefined)}
                  type="button"
                >
                  ×
                </button>
              </span>
            ))}
            <span className="mt-toolbar__sep" style={{ flex: 1 }} />
            <span className="rl-chips__count">
              <strong style={{ color: "var(--ink)" }}>{total}</strong> найдено
              {selected.size > 0 && (
                <>
                  {" · "}
                  <strong style={{ color: "var(--ink)" }}>{selected.size}</strong>{" "}
                  выбрано
                </>
              )}
            </span>
          </div>
        )}

        {/* TABLE */}
        <div className="rl-tablewrap" data-tour="term-table">
          {itemsQuery.isLoading ? (
            <div className="mt-empty">Загружаем…</div>
          ) : itemsQuery.isError ? (
            <div className="mt-empty mt-empty--error">
              {(itemsQuery.error as Error).message}
            </div>
          ) : items.length === 0 ? (
            <div className="mt-empty">Нет записей по фильтру.</div>
          ) : (
            <table className="mt-table">
              <thead>
                <tr>
                  <th className="mt-table__check">
                    <label className="mt-check">
                      <input
                        type="checkbox"
                        checked={allChecked}
                        ref={(el) => {
                          if (el) el.indeterminate = someChecked;
                        }}
                        onChange={toggleSelectAll}
                      />
                    </label>
                  </th>
                  <th style={{ width: "90px" }}>Тип</th>
                  <th style={{ width: "180px" }}>Оригинал</th>
                  <th style={{ minWidth: "220px" }}>Перевод</th>
                  <th style={{ width: "140px" }}>Домен</th>
                  <th style={{ width: "150px" }}>Источник</th>
                  <th style={{ width: "100px" }}>Conf</th>
                  <th style={{ width: "120px" }}>Статус</th>
                  <th className="mt-td--actions" style={{ width: "56px" }} />
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <Row
                    key={it.item_id}
                    item={it}
                    selected={selected.has(it.item_id)}
                    onToggle={() => toggleSelect(it.item_id)}
                    onOpen={() =>
                      navigate(
                        `/admin/terminology/${encodeURIComponent(it.item_id)}`,
                      )
                    }
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* BULK BAR */}
        {selected.size > 0 && (
          <div className="mt-bulkbar">
            <strong style={{ color: "#fff" }}>{selected.size} выбрано</strong>
            <span
              className="mt-text-tight"
              style={{ color: "rgba(255,255,255,0.6)" }}
            >
              · обновляем по полю для всех выбранных
            </span>
            <span className="mt-bulkbar__sep" />
            <button
              className="mt-btn"
              onClick={() => bulkSetField("domain")}
              disabled={bulkMutation.isPending}
              type="button"
            >
              Установить домен…
            </button>
            <button
              className="mt-btn"
              onClick={() => bulkSetField("notes")}
              disabled={bulkMutation.isPending}
              type="button"
            >
              Заметка…
            </button>
            <div
              className="mt-divider--v"
              style={{ background: "rgba(255,255,255,0.2)" }}
            />
            <button
              className="mt-btn"
              onClick={() => setSelected(new Set())}
              type="button"
            >
              Снять выделение
            </button>
          </div>
        )}

        {/* FOOT */}
        <div className="rl-foot">
          <div className="mt-pager">
            <button
              className="mt-icon-btn mt-icon-btn--bordered"
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              disabled={offset === 0}
              type="button"
              aria-label="Предыдущая"
            >
              ‹
            </button>
            <span className="mt-pager__count">
              {items.length === 0
                ? "0"
                : `${offset + 1} — ${offset + items.length}`}
              {total > 0 ? ` из ${total}` : ""}
            </span>
            <button
              className="mt-icon-btn mt-icon-btn--bordered"
              onClick={() => setOffset(offset + PAGE_SIZE)}
              disabled={offset + items.length >= total}
              type="button"
              aria-label="Следующая"
            >
              ›
            </button>
          </div>
          <span className="rl-foot__sep" />
          <span className="mt-mono mt-text-tight" style={{ fontSize: "var(--fs-xs)" }}>
            {params.toString() || "(без фильтров)"}
          </span>
          {itemsQuery.isFetching && <span className="mt-spinner" />}
        </div>
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Row({
  item,
  selected,
  onToggle,
  onOpen,
}: {
  item: TerminologyItem;
  selected: boolean;
  onToggle: () => void;
  onOpen: () => void;
}) {
  const conf = item.confidence ?? null;
  const confW = conf == null ? 0 : Math.round(conf * 100);
  const confClass =
    conf == null
      ? ""
      : conf >= 0.85
        ? " rl-confbar--high"
        : conf < 0.4
          ? " rl-confbar--low"
          : "";
  function handleRowClick(e: React.MouseEvent) {
    const target = e.target as HTMLElement;
    if (target.closest("input,label,a,button")) return;
    onOpen();
  }
  return (
    <tr
      style={{ cursor: "pointer" }}
      onClick={handleRowClick}
      className={selected ? "is-selected" : undefined}
    >
      <td className="mt-table__check">
        <label className="mt-check">
          <input type="checkbox" checked={selected} onChange={onToggle} />
        </label>
      </td>
      <td>
        <span className={`mt-itemtype mt-itemtype--${item.item_type}`}>
          {shortType(item.item_type)}
        </span>
      </td>
      <td>
        <div className="rl-cell-source" style={{ gap: 6 }}>
          {item.source_lang && (
            <span
              className="mt-review__lang-tag"
              style={{ fontSize: 10, flex: "0 0 auto" }}
            >
              {langTagLabel(item.source_lang as never)}
            </span>
          )}
          <strong>{item.source_text ?? "—"}</strong>
        </div>
      </td>
      <td>
        <div
          className="rl-cell-cand"
          style={{ gap: 6, flexDirection: "column", alignItems: "stretch" }}
        >
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {item.target_text ? (
              <>
                {item.target_lang && (
                  <span
                    className="mt-review__lang-tag"
                    style={{ fontSize: 10, flex: "0 0 auto" }}
                  >
                    {langTagLabel(item.target_lang as never)}
                  </span>
                )}
                <span>{item.target_text}</span>
              </>
            ) : (
              <span
                className="mt-text-tight mt-muted"
                style={{ fontStyle: "italic" }}
              >
                — нет перевода
              </span>
            )}
          </div>
          {item.item_type === "term_alias" && item.parent_concept && (
            <div
              className="mt-text-tight mt-muted"
              style={{ fontSize: "var(--fs-xs)", display: "flex", gap: 4 }}
            >
              <span style={{ color: "var(--muted)" }}>alias →</span>
              {item.parent_concept.item_id ? (
                <Link
                  to={`/admin/terminology/${encodeURIComponent(item.parent_concept.item_id)}`}
                  onClick={(e) => e.stopPropagation()}
                  style={{ color: "var(--blue-700)", textDecoration: "none" }}
                >
                  {item.parent_concept.source_text}
                  {item.parent_concept.target_text &&
                    ` / ${item.parent_concept.target_text}`}
                </Link>
              ) : (
                <span>
                  {item.parent_concept.source_text}
                  {item.parent_concept.target_text &&
                    ` / ${item.parent_concept.target_text}`}
                </span>
              )}
            </div>
          )}
        </div>
      </td>
      <td>{item.domain ?? "—"}</td>
      <td className="mt-td--mono mt-text-tight">{item.source_key ?? "—"}</td>
      <td className="mt-td--num">
        {conf == null ? (
          "—"
        ) : (
          <>
            {conf.toFixed(2)}{" "}
            <span
              className={"rl-confbar" + confClass}
              style={{ ["--w" as never]: `${confW}%` } as CSSProperties}
            />
          </>
        )}
      </td>
      <td>
        <span
          className={"mt-badge mt-badge--no-dot " + badgeForStatus(item.status)}
        >
          {item.status ? statusLabel(item.status) : "—"}
        </span>
      </td>
      <td className="mt-td--actions">
        <Link
          className="mt-icon-btn"
          to={`/admin/terminology/${encodeURIComponent(item.item_id)}`}
          title="Открыть"
          onClick={(e) => e.stopPropagation()}
        >
          →
        </Link>
      </td>
    </tr>
  );
}

function DomainList({
  options,
  active,
  onPick,
  mono = false,
  translateLabel,
}: {
  options: string[];
  active: string | undefined;
  onPick: (val: string | undefined) => void;
  mono?: boolean;
  translateLabel?: (v: string) => string;
}) {
  if (options.length === 0)
    return (
      <div className="mt-text-tight mt-muted" style={{ padding: "4px 8px" }}>
        —
      </div>
    );
  return (
    <div>
      {options.map((opt) => (
        <div
          key={opt}
          className={"rl-domain" + (active === opt ? " is-active" : "")}
          onClick={() => onPick(active === opt ? undefined : opt)}
        >
          <span className={mono ? "mt-mono mt-text-tight" : undefined}>
            {translateLabel ? translateLabel(opt) : opt}
          </span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function shortType(t: string): string {
  switch (t) {
    case "abbreviation":
      return "abbr";
    case "glossary_term":
      return "term";
    case "alias_candidate":
      return "alias";
    case "term_alias":
      return "alias";
    case "enforcement_candidate":
      return "enf";
    case "eval_failure":
      return "eval";
    default:
      return t;
  }
}

function badgeForStatus(status: string | null | undefined): string {
  switch (status) {
    case "approved":
      return "mt-badge--success";
    case "rejected":
      return "mt-badge--danger";
    case "needs_review":
    case "needs_second_review":
      return "mt-badge--warning";
    case "open":
      return "mt-badge--neutral";
    default:
      return "mt-badge--neutral";
  }
}

function hasActiveChips(f: ListTerminologyFilters): boolean {
  return Boolean(
    f.q ||
      f.item_type ||
      f.domain ||
      f.status ||
      f.source_key ||
      f.source_lang ||
      f.target_lang ||
      f.mode,
  );
}

function chipsList(
  f: ListTerminologyFilters,
  role?: string,
): Array<{ key: string; label: string }> {
  const chips: Array<{ key: string; label: string }> = [];
  if (f.q) chips.push({ key: "q", label: `поиск: ${f.q}` });
  if (f.item_type)
    chips.push({ key: "item_type", label: `тип: ${itemTypeLabel(f.item_type)}` });
  if (f.domain) chips.push({ key: "domain", label: `домен: ${f.domain}` });
  if (f.status)
    chips.push({ key: "status", label: `статус: ${statusLabel(f.status)}` });
  if (f.source_key)
    chips.push({ key: "source_key", label: `источник: ${f.source_key}` });
  if (role) chips.push({ key: "role", label: `роль: ${role}` });
  if (f.mode)
    chips.push({
      key: "mode",
      label:
        f.mode === "hard"
          ? "режим: hard enforcement"
          : "режим: hint only",
    });
  return chips;
}
